import * as Yup from 'yup';
import { useState } from 'react';
import NextLink from 'next/link';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useSnackbar } from 'notistack';
import { PATH_AUTH } from '../../../routes/paths';
import useAuth from '../../../hooks/useAuth';
import { BotaoAuth, CampoAuth, EntradaAuth, IconeEmail, SenhaAuth } from '../CamposAuth';

/* ============================================================================
   Formulario de login - visual novo (shadcn + Tailwind).

   NADA DA AUTENTICACAO FOI TOCADO: mesma validacao Yup, mesma chamada
   `login(email, senha)`, mesma ordem de leitura do erro
   (`errors[0].message` -> `message` -> mensagem genérica). Token, tempo de sessao
   e contrato com o mobile continuam exatamente como estavam.

   POR QUE ESTA REESCRITA VALE MAIS QUE AS OUTRAS: a tela de login tinha o estilo
   aplicado por uma FOLHA DE 40 LINHAS DE `!important` dentro de `pages/auth/
   login.jsx`, apontando para classes internas do MUI (`.MuiInputBase-root`,
   `.MuiInput-underline:before`, `.MuiInputLabel-shrink`...). Isso e frágil de um
   jeito específico: qualquer subida de versao do MUI renomeia uma dessas classes e
   a tela volta ao visual padrao sem nenhum erro de build. Agora o estilo esta no
   proprio campo, e a folha de `!important` desapareceu.

   TRES CORRECOES:

   1. `useEffect` lia `localStorage.getItem('lembrar')` e fazia
      `setValue('lembrar', ...)` num campo que NAO EXISTE no formulario (o
      checkbox "Lembrar" esta comentado desde sempre, e a gravacao tambem). Era
      leitura de storage a cada montagem para preencher um campo fantasma. Removido.

   2. O botao usava `loading={isSubmitting}` - prop que o Button do MUI v9 nao tem
      (era do LoadingButton do v5). Na pratica o botao NAO indicava carregando, e
      dava para clicar duas vezes e disparar dois logins. Agora desabilita e troca
      o rotulo.

   3. O olho de mostrar senha nao tinha rotulo acessivel. Agora tem `aria-label` e
      `aria-pressed`.

   4. (19/08/2026) Os dois campos eram COPIA dos de CamposAuth, com o botao de
      mostrar senha escrito "ver"/"ocultar" em texto e os icones em emoji (✉ 🔒).
      Agora usam `EntradaAuth` e `SenhaAuth` - o olho e SVG, e o campo de senha
      desta tela nao pode mais divergir do das outras tres.
   ============================================================================ */

export default function LoginForm() {
  const { login } = useAuth();
  const { enqueueSnackbar } = useSnackbar();

  /* TREMOR NO ERRO (escolhido em 20/08/2026).

     Motivo pratico: a mensagem de erro aparece num snackbar no CANTO da tela,
     longe de onde a pessoa esta olhando (o botao Entrar). Quem clica e continua
     encarando o formulario ve... o formulario, igual. O tremor traz o aviso para
     onde o olho ja esta.

     Duas oscilacoes e para - erro que treme sem parar vira alarme. O estado
     volta a false depois de 420ms (a animacao dura 400ms) para o proximo erro
     poder tremer de novo; sem isso, a classe ficaria aplicada e o segundo erro
     nao animaria nada. */
  const [tremendo, setTremendo] = useState(false);

  const acusarErro = () => {
    setTremendo(false);
    // Dois quadros de intervalo: reaplicar a classe no MESMO quadro nao
    // reinicia a animacao - o navegador nao ve mudanca.
    requestAnimationFrame(() => requestAnimationFrame(() => setTremendo(true)));
    setTimeout(() => setTremendo(false), 420);
  };

  const LoginSchema = Yup.object().shape({
    email: Yup.string().email('O e-mail deve ser um endereço de e-mail válido').required('O e-mail é obrigatório'),
    password: Yup.string().required('Senha requerida'),
  });

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(LoginSchema),
    defaultValues: { email: '', password: '' },
  });

  const onSubmit = async (data) => {
    try {
      await login(data.email, data.password);
    } catch (err) {
      const error = err?.response?.data;
      console.error(error);

      acusarErro();

      if (error?.errors || error?.message) {
        if (error?.errors && error?.errors.length > 0)
          enqueueSnackbar(error?.errors[0].message, { variant: 'error', style: { whiteSpace: 'pre-line' } });
        else if (error?.message)
          enqueueSnackbar(error?.message, { variant: 'error', style: { whiteSpace: 'pre-line' } });
        else
          enqueueSnackbar(`Ops ocorreu um erro no sistema.`, { variant: 'error', style: { whiteSpace: 'pre-line' } });
      }
    }
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className={`space-y-3.5 ${tremendo ? 'animate-tremer' : ''}`}
      noValidate
    >
      {/* ENTRADA ESCALONADA: cada bloco sobe 8px enquanto aparece, 60ms depois do
          anterior. O atraso vai em `style` porque e um valor por posicao - virar
          classe daria seis utilitarias que existem para um lugar so.
          `animate-subir` usa `both`, entao o estado final fica aplicado: se a
          animacao nao rodar (reduzir movimento), o conteudo aparece pronto. */}
      <div className="animate-subir [animation-delay:60ms]">
        <CampoAuth id="login-email" rotulo="Endereço de email" erro={errors.email?.message}>
        <EntradaAuth
          id="login-email"
          type="email"
          autoComplete="email"
          icone={<IconeEmail />}
          erro={errors.email}
          placeholder="seu.email@empresa.com.br"
          {...register('email')}
          />
        </CampoAuth>
      </div>

      <div className="animate-subir [animation-delay:120ms]">
        <SenhaAuth
          id="login-senha"
          rotulo="Senha"
          erro={errors.password?.message}
          autoComplete="current-password"
          registro={register('password')}
        />
      </div>

      {/* HIERARQUIA NOS LINKS (escolhida em 20/08/2026).

          Antes os dois ficavam na MESMA linha, com o mesmo peso e a mesma cor,
          empurrados para os cantos - dois caminhos disputando a atencao logo
          abaixo dos campos, e nenhum deles e a acao principal (o botao e).

          Agora "Esqueceu sua senha?" fica sozinho na linha, alinhado ao campo
          acima, e o pedido de acesso desce um degrau: fonte menor, cor de texto
          secundario, e a frase diz o que a pessoa procura ("Não tem acesso?") em
          vez de prometer o que o sistema nao faz - aqui a conta nasce PENDENTE de
          liberacao, ninguem "cria conta" e entra. */}
      <div className="animate-subir pt-1 [animation-delay:180ms]">
        <NextLink href={PATH_AUTH.resetPassword} className="text-[12.5px] font-semibold text-primary hover:underline">
          Esqueceu sua senha?
        </NextLink>
      </div>

      <div className="animate-subir [animation-delay:240ms]">
        <BotaoAuth carregando={isSubmitting} rotuloCarregando="Entrando...">
          Entrar
        </BotaoAuth>
      </div>

      <div className="animate-subir text-center [animation-delay:300ms]">
        <span className="text-[12px] text-muted-foreground">Não tem acesso? </span>
        <NextLink href={PATH_AUTH.register} className="text-[12px] font-semibold text-primary hover:underline">
          Solicitar acesso
        </NextLink>
      </div>
    </form>
  );
}
